// src/pages/UnlabelledStockList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaList,
  FaThLarge,
} from "react-icons/fa";
import { FiRotateCcw, FiMoreVertical } from "react-icons/fi";
import { TbArrowsSort } from "react-icons/tb";
import Select from "react-select";
import toast from "react-hot-toast";

import ConfirmDialog from "../components/ConfirmDialog";

import {
  useGetAllUnlabelledStockQuery,
  useDeleteUnlabelledItemMutation,
  useBulkDeleteUnlabelledMutation,
} from "../toolkitServices/unlabelledApi";

// masters
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";

import { useDispatch, useSelector } from "react-redux";
import { setUnlabelledList } from "../slices/unlabelledStockSlice";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";

import { handleDropDowns } from "../config/dropDownHelpers";
import { fmtDate } from "../utils/date";

/* ------------ Tiny reusable spinner (same vibe as Labelled/Sales) ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ----------------- helpers ----------------- */
const toTitle = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// react-select styling: compact, same as Labelled
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "0.8rem",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#111827",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
      ? "#ddd6fe"
      : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

/* ------------- skeleton rows (table) ------------- */
const SkeletonRows = ({ cols = 11, rows = 8, actionColumn = false }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td
            key={`sk-${r}-${c}`}
            className={
              actionColumn && c === cols - 1
                ? `${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] border-l border-violet-100 bg-violet-50 sm:w-[104px] sm:min-w-[104px]`
                : dataCell
            }
          >
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ------------- GRID cards skeleton ------------- */
const GridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={`sk-card-${i}`}
        className="border rounded-2xl bg-white shadow-sm overflow-hidden animate-pulse"
      >
        <div className="aspect-[4/3] bg-slate-200" />
        <div className="p-3 space-y-2">
          <div className="h-4 w-24 bg-slate-200 rounded" />
          <div className="h-3 w-32 bg-slate-200 rounded" />
          <div className="h-3 w-20 bg-slate-200 rounded" />
        </div>
      </div>
    ))}
  </div>
);

/* ---------------------------- MAIN COMPONENT ---------------------------- */

const UnlabelledStockList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // header controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // same pattern as Labelled
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // sorting
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  // list / grid view
  const [viewMode, setViewMode] = useState("table"); // "table" | "grid"

  // filters (draft vs applied)
  const emptyFilter = {
    vendorId: "",
    branchId: "",
    categoryId: "",
    productId: "",
    designId: "",
    purityId: "",
    dateFrom: "",
    dateTo: "",
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyFilter);
  const [draftFilters, setDraftFilters] = useState(emptyFilter);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // for auto-focus in filter, if needed later
  const vendorSelectRef = useRef(null);

  // ✅ selections across pages: Map<id, itemSnapshot>
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);

  // confirmation dialog state
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'single' | 'bulk'
    payload: null, // { id, label } or { ids, count }
  });

  // masters
  const { data: vendors } = useGetAllVendorsQuery();
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();

  // push masters to store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, categories, products, vendors, designs, purities, branches]);

  // state selectors
  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const vendorsFromState = useSelector((s) => s.vendors?.vendors);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  // query (uses applied filters ONLY)
  const { data, isFetching, isLoading, isSuccess } =
    useGetAllUnlabelledStockQuery({
      page,
      limit,
      search,
      status: statusFilter, // extra filter like labelled
      sortBy,
      sortOrder,
      ...appliedFilters,
    });

  const rowsFromStore =
    useSelector((state) => state?.unlabelledStocks?.unlabelledStock) || [];

  useEffect(() => {
    if (isSuccess && Array.isArray(data?.data)) {
      dispatch(setUnlabelledList(data.data));
    }
  }, [isSuccess, data, dispatch]);

  // keyboard: Ctrl+P to go to create
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/stocks/unlabelled-stock-list/create-unlabelled-stock");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // search / select / sort / filters debounce → page 1
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, limit, appliedFilters, sortBy, sortOrder]);

  // pagination meta
  const totalDocs = data?.pagination?.totalDocs ?? data?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? data?.pages ?? 1;
  const hasPrev = data?.pagination?.hasPrev ?? page > 1;
  const hasNext = data?.pagination?.hasNext ?? page < totalPages;

  // ✅ Unlabelled: allow delete unless explicitly blocked
  const isDeletable = (it) => it?.isActive !== false;

  // ---- dropdown options ----
  const findOpt = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v)) || null;

  const vendorOptions = useMemo(
    () => handleDropDowns({ key: "vendor", vendors: vendorsFromState }),
    [vendorsFromState]
  );
  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState]
  );
  const categoryOptions = useMemo(
    () => handleDropDowns({ key: "category", categories: categoriesFromState }),
    [categoriesFromState]
  );

  const vendorValue = useMemo(
    () => findOpt(vendorOptions, draftFilters.vendorId),
    [vendorOptions, draftFilters.vendorId]
  );
  const branchValue = useMemo(
    () => findOpt(branchOptions, draftFilters.branchId),
    [branchOptions, draftFilters.branchId]
  );
  const categoryValue = useMemo(
    () => findOpt(categoryOptions, draftFilters.categoryId),
    [categoryOptions, draftFilters.categoryId]
  );

  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: productsFromState,
        category: categoryValue,
      }),
    [productsFromState, categoryValue]
  );
  const productValue = useMemo(
    () => findOpt(productOptions, draftFilters.productId),
    [productOptions, draftFilters.productId]
  );

  const designOptions = useMemo(
    () =>
      handleDropDowns({
        key: "design",
        designs: designFromState,
        product: productValue,
      }),
    [designFromState, productValue]
  );
  const designValue = useMemo(
    () => findOpt(designOptions, draftFilters.designId),
    [designOptions, draftFilters.designId]
  );

  const purityOptions = useMemo(
    () =>
      handleDropDowns({
        key: "purity",
        purities: purityFromState,
        category: categoryValue,
      }),
    [purityFromState, categoryValue]
  );
  const purityValue = useMemo(
    () => findOpt(purityOptions, draftFilters.purityId),
    [purityOptions, draftFilters.purityId]
  );

  // handlers (draft)
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((s) => ({ ...s, [name]: value }));
  };
  const onVendorChange = (opt) =>
    setDraftFilters((s) => ({ ...s, vendorId: opt?.value || "" }));
  const onBranchChange = (opt) =>
    setDraftFilters((s) => ({ ...s, branchId: opt?.value || "" }));
  const onCategoryChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      categoryId: opt?.value || "",
      productId: "",
      designId: "",
      purityId: "",
    }));
  const onProductChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      productId: opt?.value || "",
      designId: "",
    }));
  const onDesignChange = (opt) =>
    setDraftFilters((s) => ({ ...s, designId: opt?.value || "" }));
  const onPurityChange = (opt) =>
    setDraftFilters((s) => ({ ...s, purityId: opt?.value || "" }));

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };
  const resetDraftFilters = () => setDraftFilters(emptyFilter);

  // chips: labels for applied filters
  const labelFromOpts = (opts, v) => findOpt(opts, v)?.label || "";
  const chips = useMemo(() => {
    const list = [];
    if (statusFilter)
      list.push({ key: "status", label: `Status: ${toTitle(statusFilter)}` });
    if (appliedFilters.vendorId)
      list.push({
        key: "vendorId",
        label: `Vendor: ${labelFromOpts(
          vendorOptions,
          appliedFilters.vendorId
        )}`,
      });
    if (appliedFilters.branchId)
      list.push({
        key: "branchId",
        label: `Branch: ${labelFromOpts(
          branchOptions,
          appliedFilters.branchId
        )}`,
      });
    if (appliedFilters.categoryId)
      list.push({
        key: "categoryId",
        label: `Category: ${labelFromOpts(
          categoryOptions,
          appliedFilters.categoryId
        )}`,
      });
    if (appliedFilters.productId)
      list.push({
        key: "productId",
        label: `Product: ${labelFromOpts(
          productOptions,
          appliedFilters.productId
        )}`,
      });
    if (appliedFilters.designId)
      list.push({
        key: "designId",
        label: `Design: ${labelFromOpts(
          designOptions,
          appliedFilters.designId
        )}`,
      });
    if (appliedFilters.purityId)
      list.push({
        key: "purityId",
        label: `Purity: ${labelFromOpts(
          purityOptions,
          appliedFilters.purityId
        )}`,
      });
    if (appliedFilters.dateFrom)
      list.push({
        key: "dateFrom",
        label: `From: ${appliedFilters.dateFrom}`,
      });
    if (appliedFilters.dateTo)
      list.push({ key: "dateTo", label: `To: ${appliedFilters.dateTo}` });
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [
    appliedFilters,
    statusFilter,
    search,
    vendorOptions,
    branchOptions,
    categoryOptions,
    productOptions,
    designOptions,
    purityOptions,
  ]);

  const clearChip = (key) => {
    if (key === "status") setStatusFilter("");
    else if (key === "search") setSearch("");
    else setAppliedFilters((s) => ({ ...s, [key]: "" }));
    setPage(1);
  };

  const clearAllChips = () => {
    setSearch("");
    setStatusFilter("");
    setAppliedFilters(emptyFilter);
    setPage(1);
  };

  // sorting click
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  // ✅ selection helpers (global)
  const selectedRows = useMemo(
    () => Array.from(selectedItems.values()),
    [selectedItems]
  );
  const selectedCount = selectedRows.length;

  const showActions = selectedCount === 0;
  const columnCount = showActions ? 11 : 10; // checkbox + data + (optional) Action

  const toggleSelect = (item) => {
    if (!item?._id) return;
    const key = String(item._id);
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, item);
      }
      return next;
    });
  };

  const removeSelection = (id) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(String(id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedItems(new Map());
  };

  // "select all" per page (only deletable items)
  const deletableItemsOnPage = (rowsFromStore || []).filter(isDeletable);

  const allSelectedOnPage =
    deletableItemsOnPage.length > 0 &&
    deletableItemsOnPage.every((it) => selectedItems.has(String(it._id)));

  const toggleSelectAllOnPage = () => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (allSelectedOnPage) {
        deletableItemsOnPage.forEach((it) => next.delete(String(it._id)));
      } else {
        deletableItemsOnPage.forEach((it) => next.set(String(it._id), it));
      }
      return next;
    });
  };

  // mutations
  const [deleteItem, { isLoading: deletingOne }] =
    useDeleteUnlabelledItemMutation();
  const [bulkDelete, { isLoading: deletingMany }] =
    useBulkDeleteUnlabelledMutation();

  // confirm handlers
  const openConfirmSingle = (item) => {
    if (!isDeletable(item)) return;
    setConfirm({
      open: true,
      mode: "single",
      payload: {
        id: item._id,
        label: item.productName || item.designName || item._id,
      },
    });
  };

  const openConfirmBulk = () => {
    const ids = Array.from(selectedItems.keys());
    if (!ids.length) return;
    setConfirm({
      open: true,
      mode: "bulk",
      payload: { ids, count: ids.length },
    });
  };

  const closeConfirm = () =>
    setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    try {
      if (confirm.mode === "single") {
        const { id, label } = confirm.payload || {};
        await deleteItem(id).unwrap();
        toast.success(`Deleted ${label}`);
        removeSelection(id);
      } else if (confirm.mode === "bulk") {
        const { ids, count } = confirm.payload || {};
        const res = await bulkDelete(ids).unwrap();
        const removed = res?.deleted ?? count;
        const skipped = (res?.blocked || []).length;
        toast.success(
          `Deleted ${removed}${skipped ? ` • Skipped ${skipped}` : ""}`
        );
        clearSelection();
      }
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Operation failed";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  const showInitialOverlay =
    isLoading && (!rowsFromStore || rowsFromStore.length === 0);

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* 🔄 Global overlay loader – same feel as LabelledList */}
      {showInitialOverlay && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading unlabelled stock…</span>
          </div>
        </div>
      )}

      {/* Top header bar – matched with LabelledStockList */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight">
            Unlabelled Stock
          </h2>
          {isFetching && rowsFromStore.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/stocks/unlabelled-stock-list/create-unlabelled-stock"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-4 py-1.5 text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Filters bar – compact like LabelledStockList */}
      <div className="px-2 sm:px-4 py-2 border-b bg-slate-50/80">
        <div className="flex items-end gap-2 sm:flex-wrap sm:gap-3">
          {/* Status filter */}
          <div className="flex w-[70px] shrink-0 flex-col gap-[1px] sm:w-[130px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              Status
            </label>
            <select
              aria-label="Filter by status"
              title="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="h-8 w-full rounded-lg border border-slate-200 px-1 text-[10px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-2.5 sm:text-[11px]"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="sold">Sold</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* View toggle */}
          <div className="flex w-[66px] shrink-0 flex-col gap-[1px] sm:w-[150px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              View
            </label>
            <div className="flex h-8 overflow-hidden rounded-full border border-slate-200 bg-white text-[11px]">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-full px-1 font-medium outline-[#6b59c6] transition sm:px-3 ${
                  viewMode === "table"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                title="List view"
                aria-label="List view"
              >
                <FaList className="text-[11px]" />
                <span className="hidden sm:inline">Table</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-full px-1 font-medium outline-[#6b59c6] transition sm:px-3 ${
                  viewMode === "grid"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                title="Grid view"
                aria-label="Grid view"
              >
                <FaThLarge className="text-[11px]" />
                <span className="hidden sm:inline">Grid</span>
              </button>
            </div>
          </div>

          {/* Search text */}
          <div className="flex min-w-0 flex-1 flex-col gap-[1px] sm:min-w-[160px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              Search
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 min-w-0 w-full rounded-lg border border-slate-200 px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
            />
          </div>

          {/* Filters button + bulk menu */}
          <div className="flex w-auto shrink-0 flex-col gap-[1px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block sm:invisible">
              Filters
            </label>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={openFilters}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center gap-1 rounded-full border border-violet-200 bg-white text-[11px] text-[#6b59c6] outline-[#6b59c6] hover:bg-violet-50 sm:w-auto sm:px-3"
                title="Advanced filters"
                aria-label="Advanced filters"
              >
                <FaFilter /> <span className="hidden sm:inline">Filters</span>
              </button>

              {/* Bulk menu (only delete here, for now) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setBulkMenuOpen((p) => !p)}
                  className="flex items-center justify-center h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-slate-50 outline-[#6b59c6]"
                  title="Bulk actions"
                >
                  <FiMoreVertical className="text-sm text-slate-600" />
                </button>

                {bulkMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white text-gray-800 rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden">
                    <div className="px-3 py-2 text-[11px] text-slate-500 border-b bg-slate-50/70 flex items-center justify-between">
                      <span className="font-semibold tracking-wide">
                        Bulk actions
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="h-5 min-w-[20px] px-2 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold flex items-center justify-center">
                          {selectedCount || 0}
                        </span>
                        <span className="text-[10px]">selected</span>
                      </span>
                    </div>

                    {/* Danger zone / Delete */}
                    <div className="bg-rose-50/40">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-rose-500/80">
                        Danger zone
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          setBulkMenuOpen(false);
                          openConfirmBulk();
                        }}
                        disabled={!selectedCount || deletingMany}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed text-rose-600 flex items-center gap-2"
                      >
                        <FaTrashAlt className="text-xs" />
                        <span>Delete selected</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ✅ Selected items bar (like Labelled) */}
      {selectedCount > 0 && (
        <div className="px-4 py-2.5 border-b bg-gradient-to-r from-violet-50 via-indigo-50 to-slate-50">
          <div className="flex items-center gap-3">
            {/* Left: count + text */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="h-6 w-6 rounded-full bg-[#6b59c6] text-white flex items-center justify-center text-[11px] shadow-sm">
                {selectedCount}
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[11px] font-semibold text-slate-800">
                  {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
                </span>
                <span className="text-[10px] text-slate-500">
                  Use bulk actions to delete selected items
                </span>
              </div>
            </div>

            {/* Middle: scrollable chips with product/design */}
            <div className="flex-1 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max pr-2">
                {selectedRows.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-violet-100 text-[11px] text-slate-700 shadow-[0_1px_3px_rgba(15,23,42,0.06)] hover:border-[#6b59c6]/70 hover:bg-violet-50/80 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <span className="font-medium text-[#6b59c6]">
                      {item.productName || "—"}
                    </span>
                    {item.designName && (
                      <span className="text-[10px] text-slate-500">
                        · {item.designName}
                      </span>
                    )}

                    <FaTimesCircle
                      className="ml-1 text-[10px] text-slate-300 group-hover:text-rose-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelection(item._id);
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: clear all */}
            <button
              type="button"
              onClick={clearSelection}
              className="ml-2 text-[11px] text-rose-500 hover:text-rose-600 hover:bg-rose-50 px-3 py-1 rounded-full border border-rose-100 bg-white flex items-center gap-1 flex-shrink-0"
            >
              <FaTimesCircle className="text-[11px]" />
              <span>Clear all</span>
            </button>
          </div>
        </div>
      )}

      {/* ===== Main Content: TABLE or GRID ===== */}
      {viewMode === "table" ? (
        // ---------- TABLE VIEW ----------
        <div
          className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto"
          aria-busy={isFetching}
        >
          <table className="min-w-full text-xs sm:text-sm text-left">
            <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[10px] uppercase text-[#4b3d91] sm:text-[11px]">
              <tr>
                {/* Select all (only deletable on current page) */}
                <th className="px-3 py-1.5 w-10">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={toggleSelectAllOnPage}
                    className="h-4 w-4"
                    title="Select all deletable on page"
                  />
                </th>

                <th className={headerCell}>
                  Product
                </th>
                <th className={headerCell}>
                  Category
                </th>
                <th className={headerCell}>
                  Design
                </th>
                <th className={headerCell}>
                  Purity 
                </th>
                <th className={`${headerCell} text-right`}>
                  Total Wt {sortIcon("totalWt")}
                </th>
                <th className={`${headerCell} text-right`}>
                  Net Wt {sortIcon("netWt")}
                </th>
                <th className={headerCell}>
                  Branch 
                </th>
                 <th className={headerCell}>
                  Counter
                </th>
                <th className={headerCell}>
                  Status 
                </th>
                <th className={headerCell}>
                  Created On {sortIcon("createdAt")}
                </th>
                {/* action column only when no selection */}
                {showActions && (
                  <th
                    className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}
                  >
                    <span className="sm:hidden">Act</span>
                    <span className="hidden sm:inline">Action</span>
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="bg-white text-gray-700">
              <AnimatePresence initial={false}>
                {isFetching && rowsFromStore.length ? (
                  <SkeletonRows
                    cols={columnCount}
                    rows={8}
                    actionColumn={showActions}
                  />
                ) : rowsFromStore?.length > 0 ? (
                  rowsFromStore.map((item, index) => {
                    const deletable = isDeletable(item);
                    const checked = selectedItems.has(String(item._id));
                    const status = item?.status ?? "";

                    return (
                      <motion.tr
                        key={item._id || `${item.productName}-${index}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className="group border-t transition hover:bg-[#f4f0ff]"
                      >
                        {/* row checkbox */}
                        <td className="px-3 sm:px-4 py-1.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            disabled={!deletable}
                            title={
                              !deletable
                                ? "This item is protected from deletion"
                                : "Select item"
                            }
                            onChange={() => toggleSelect(item)}
                          />
                        </td>

                        <td className={dataCell}>{item.productName}</td>
                        <td className={dataCell}>{item.categoryName}</td>
                        <td className={dataCell}>{item.designName}</td>
                        <td className={dataCell}>{item.purityName}</td>
                        <td className={`${dataCell} text-right`}>
                          {item.totalWt}
                        </td>
                        <td className={`${dataCell} text-right`}>
                          {item.netWt}
                        </td>
                        <td className={dataCell}>{item.branchName}</td>
                        <td className={dataCell}>{item.counterName}</td>
                        <td className={dataCell}>
                          {status ? (
                            <span
                              className={`px-2 py-0.5 rounded-full text-[11px] ${
                                status === "active"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : status === "sold"
                                  ? "bg-rose-100 text-rose-700"
                                  : status === "inactive"
                                  ? "bg-slate-200 text-slate-700"
                                  : "bg-violet-100 text-violet-700"
                              }`}
                            >
                              {toTitle(status)}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className={dataCell}>{fmtDate(item.createdAt)}</td>

                        {/* 👇 Action cell only when no selection */}
                        {showActions && (
                          <td
                            className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                          >
                            <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Edit"
                              onClick={() =>
                                navigate(
                                  `/stocks/unlabelled-stock-list/edit/${item._id}`
                                )
                              }
                            >
                              <FaPencilAlt />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                              title="Delete"
                              disabled={deletingOne || !deletable}
                              onClick={() => openConfirmSingle(item)}
                            >
                              <FaTrashAlt />
                            </motion.button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    );
                  })
                ) : (
                  !isLoading &&
                  !isFetching && (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td
                        colSpan={columnCount}
                        className="px-4 py-8 text-center text-xs text-slate-500"
                      >
                        No matching records found. Try adjusting filters or
                        search text.
                      </td>
                    </motion.tr>
                  )
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      ) : (
        // ---------- GRID VIEW ----------
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {isFetching && rowsFromStore.length ? (
            <GridSkeleton count={8} />
          ) : rowsFromStore.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {rowsFromStore.map((item, index) => {
                const deletable = isDeletable(item);
                const checked = selectedItems.has(String(item._id));
                const status = item?.status ?? "";

                return (
                  <motion.div
                    key={item._id || `${item.productName}-${index}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                    className="relative border rounded-2xl bg-white shadow-sm overflow-hidden flex flex-col"
                  >
                    {/* Checkbox */}
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 bg-white"
                        checked={checked}
                        disabled={!deletable}
                        onChange={() => toggleSelect(item)}
                      />
                    </div>

                    {/* Status pill */}
                    <div className="absolute top-2 right-2 z-10">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : status === "sold"
                            ? "bg-rose-100 text-rose-700"
                            : status === "inactive"
                            ? "bg-slate-200 text-slate-700"
                            : "bg-violet-100 text-violet-700"
                        }`}
                      >
                        {toTitle(status || "")}
                      </span>
                    </div>

                    {/* Simple placeholder area (no image for unlabelled) */}
                    <div className="w-full aspect-[4/3] bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                      No image
                    </div>

                    <div className="p-3 flex flex-col gap-2 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-900 line-clamp-1">
                          {item.productName || "Item"}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {fmtDate(item.createdAt)}
                        </div>
                      </div>

                      <div className="text-[11px] text-slate-500 line-clamp-2">
                        {item.categoryName || "-"} ·{" "}
                        {item.designName || "Design"} ·{" "}
                        {item.purityName || "Purity"}
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Total Wt
                          </div>
                          <div className="font-semibold">
                            {item.totalWt ?? "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Net Wt
                          </div>
                          <div className="font-semibold">
                            {item.netWt ?? "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400">
                            Branch
                          </div>
                          <div className="font-semibold line-clamp-1">
                            {item.branchName || "-"}
                          </div>
                        </div>
                      </div>

                      {/* Card actions */}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/stocks/unlabelled-stock-list/edit/${item._id}`
                            )
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-[11px] text-violet-700 hover:bg-violet-100"
                        >
                          <FaPencilAlt className="text-[10px]" />
                          Edit
                        </button>

                        <button
                          type="button"
                          title="Delete"
                          disabled={deletingOne || !deletable}
                          onClick={() => openConfirmSingle(item)}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-50 text-rose-500 hover:bg-rose-50 disabled:opacity-40"
                        >
                          <FaTrashAlt className="text-[11px]" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs text-slate-500">
              No unlabelled stock found. Try adjusting filters.
            </div>
          )}
        </div>
      )}

      {/* Footer / Pagination – same style as Labelled */}
      <div className="shrink-0 border-t px-2 py-1.5 sm:px-4 sm:py-2 bg-slate-50 rounded-b-2xl flex items-center justify-between gap-2 sm:gap-3 text-[10px] sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {rowsFromStore.length ? (page - 1) * limit + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {rowsFromStore.length
                ? (page - 1) * limit + rowsFromStore.length
                : 0}
            </span>{" "}
            of <span className="font-semibold">{totalDocs}</span> entries
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => setLimit(Number(option?.value) || 10)}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={!hasPrev || isFetching || isLoading}
              onClick={() => hasPrev && setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{
                    value: totalPages ? page : 1,
                    label: String(totalPages ? page : 1),
                  }}
                  options={Array.from(
                    { length: Math.max(1, totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching || isLoading}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {Math.max(1, totalPages)}</span>
              </span>
              <span className="hidden items-center gap-1 sm:flex">
                <span>Page</span>
                <Select
                  value={{
                    value: totalPages ? page : 1,
                    label: String(totalPages ? page : 1),
                  }}
                  options={Array.from(
                    { length: Math.max(1, totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching || isLoading}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[58px]"
                  aria-label="Select page number"
                />
                <span>of {Math.max(1, totalPages)}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={!hasNext || isFetching || isLoading}
              onClick={() =>
                hasNext &&
                setPage((p) =>
                  totalPages ? Math.min(totalPages, p + 1) : p + 1,
                )
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Modal – same layout as Labelled */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Refine unlabelled stock by vendor, branch, category and
                    date range.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-4 text-[11px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Vendor */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Vendor
                    </label>
                    <Select
                      ref={vendorSelectRef}
                      styles={selectStyles}
                      value={vendorValue}
                      onChange={onVendorChange}
                      options={vendorOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Branch */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Branch
                    </label>
                    <Select
                      styles={selectStyles}
                      value={branchValue}
                      onChange={onBranchChange}
                      options={branchOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Category
                    </label>
                    <Select
                      styles={selectStyles}
                      value={categoryValue}
                      onChange={onCategoryChange}
                      options={categoryOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Product */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Product
                    </label>
                    <Select
                      styles={selectStyles}
                      value={productValue}
                      onChange={onProductChange}
                      options={productOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Design */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Design
                    </label>
                    <Select
                      styles={selectStyles}
                      value={designValue}
                      onChange={onDesignChange}
                      options={designOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* Purity */}
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Purity
                    </label>
                    <Select
                      styles={selectStyles}
                      value={purityValue}
                      onChange={onPurityChange}
                      options={purityOptions}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">
                      From date
                    </label>
                    <input
                      type="date"
                      name="dateFrom"
                      value={draftFilters.dateFrom}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">
                      To date
                    </label>
                    <input
                      type="date"
                      name="dateTo"
                      value={draftFilters.dateTo}
                      onChange={handleDateChange}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetDraftFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="px-3 py-1.5 rounded-md bg-[#6b59c6] text-white text-[11px] font-semibold hover:bg-[#5b4ab3]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm dialog – same API as Labelled list */}
      <ConfirmDialog
        open={confirm.open}
        title={
          confirm.mode === "bulk"
            ? "Delete selected items?"
            : "Delete this item?"
        }
        description={
          confirm.mode === "bulk"
            ? `This will delete ${confirm.payload?.count || 0} selected items.`
            : `Are you sure you want to delete ${
                confirm.payload?.label || "this item"
              }?`
        }
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        loading={deletingOne || deletingMany}
      />
    </div>
  );
};

export default UnlabelledStockList;
